import { useEffect, useRef, useState } from "react";
import {
  AppBox,
  AppGeneralSearch,
  AppIconButton,
  AppBorderBox,
  AppAvatar,
  AppTitle,
  AppText,
  AppLink,
  AppDivider,
  AppButton,
} from "../../../../commons/components";
import IconMultiselect from "../../../../commons/components/icons/multiselect";
import { FootballTeamPlayer } from "../../../../commons/models/football/football-team-player";
import { IFootballTeam } from "../../../../commons/models/football/interface/i-football-team";
import { toSentenceCase } from "../../../../commons/utilities/string-utils";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { cleanUpManageTeamPlayers } from "../../../../redux/slices/football/admin/team/squad/manage-team-players-slice";
import { setGeneralCleanSearchField, cleanUpGeneralSearchTitle } from "../../../../redux/slices/general-search-slice";
import { fetchManageMatchTeamPlayers } from "../../../../redux/slices/football/admin/season/match/manage-season-match-team-players-slice";
import { FootballMatchDetail } from "../../../../commons/models/football/football-match-detail";

interface MatchTeamPlayerSearchProps {
  onSelectedTeamPlayer?: (teamPlayers: FootballTeamPlayer[]) => void;
  withMultiSelect?: boolean;
  match: FootballMatchDetail;
  team: IFootballTeam;
  hostTeamId?: number;
  userSelectedTeamPlayers?: FootballTeamPlayer[];
}

export default function MatchTeamPlayerSearch({
  onSelectedTeamPlayer,
  match,
  team,
  withMultiSelect,
  hostTeamId,
  userSelectedTeamPlayers = [],
}: MatchTeamPlayerSearchProps) {
  const dispatch = useAppDispatch();
  const multiSelectRef = useRef<HTMLInputElement>(null);

  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedTeamPlayers, setSelectedTeamPlayers] = useState<FootballTeamPlayer[]>([]);
  const [searchedTeamPlayers, setSearchedTeamPlayers] = useState<FootballTeamPlayer[]>([]);

  const { manageMatchTeamPlayers } = useAppSelector((state) => state.footballManageSeasonMatchTeamPlayers);
  const { generalSearchTitle } = useAppSelector((state) => state.generalSearch);

  useEffect(() => {
    dispatch(setGeneralCleanSearchField(true));
    dispatch(cleanUpGeneralSearchTitle());
    dispatch(cleanUpManageTeamPlayers());

    dispatch(
      fetchManageMatchTeamPlayers({
        seasonId: match.season.id,
        matchId: match.id,
        teamId: team.id,
        urlParams: { hostTeamId },
      })
    );
  }, []);

  const availablePlayers = (() => {
    const selectedIds = new Set(userSelectedTeamPlayers.map((tp) => tp.player.id));
    const players = manageMatchTeamPlayers.all?.() || [];

    return players
      .filter((player) => !selectedIds.has(player.player.id))
      .sort(
        (a, b) =>
          ["GOALKEEPER", "DEFENDER", "MIDFIELDER", "FORWARD"].indexOf(a.position) -
          ["GOALKEEPER", "DEFENDER", "MIDFIELDER", "FORWARD"].indexOf(b.position)
      );
  })();

  useEffect(() => {
    if (!generalSearchTitle) {
      setSearchedTeamPlayers(availablePlayers);
      return;
    }

    const searchLower = generalSearchTitle.toLowerCase();
    const result = availablePlayers.filter(
      (tp) =>
        tp.player.fullName.toLowerCase().includes(searchLower) ||
        tp.player.displayName.toLowerCase().includes(searchLower)
    );

    setSearchedTeamPlayers(result);
  }, [generalSearchTitle, manageMatchTeamPlayers]);

  useEffect(() => {
    if (!isMultiSelect) {
      setSelectedTeamPlayers([]);
    }
  }, [isMultiSelect]);

  const togglePlayerSelection = (checked: boolean, player: FootballTeamPlayer) => {
    setSelectedTeamPlayers((prev) => (checked ? [...prev, player] : prev.filter((p) => p.id !== player.id)));
  };

  const renderPlayerRow = (player: FootballTeamPlayer, isSelected: boolean) =>
    isMultiSelect ? (
      <AppBorderBox key={player.id} border={["Bottom"]} py="xs">
        <AppBox justifyContent="space-between" alignItems="center" pr="xs" className="flex-1">
          <AppBox gap="xs" alignItems="center">
            <AppAvatar username={player.player.fullName} src={player.player.avatar || ""} size="md" />
            <AppBox flexDirection="column">
              <AppTitle as="h6">{player.player.displayName}</AppTitle>
              <AppText as="span" fontWeight="semibold" size="sm">
                {toSentenceCase(player.position)}
              </AppText>
            </AppBox>
          </AppBox>
          <label className="ee-checkbox">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => togglePlayerSelection(e.target.checked, player)}
            />
            <span className="checkmark" />
          </label>
        </AppBox>
      </AppBorderBox>
    ) : (
      <AppLink key={player.id} onClick={(e) => (e.preventDefault(), onSelectedTeamPlayer?.([player]))}>
        <AppBorderBox border={["Bottom"]} py="xs">
          <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
            <AppBox gap="xs" alignItems="center">
              <AppAvatar username={player.player.fullName} src={player.player.avatar || ""} size="md" />
              <AppBox flexDirection="column">
                <AppTitle as="h6">{player.player.displayName}</AppTitle>
                <AppBox gap="3xs">
                  <AppText as="span" fontWeight="semibold" size="sm">
                    {player.jerseyNumber}
                  </AppText>
                  <AppDivider color="dark" orientation="vertical" />
                  <AppText as="span" fontWeight="semibold" size="sm">
                    {toSentenceCase(player.position)}
                  </AppText>
                </AppBox>
              </AppBox>
            </AppBox>
          </AppBox>
        </AppBorderBox>
      </AppLink>
    );

  return (
    <AppBox flexDirection="column" gap="md">
      <AppBox gap="xs">
        <AppBox className="w-100 flex-1">
          <AppGeneralSearch />
        </AppBox>
        {withMultiSelect && (
          <>
            <AppIconButton
              icon={<IconMultiselect />}
              variant="light"
              onClick={(e) => {
                e.preventDefault();
                multiSelectRef.current?.click();
              }}
              color={isMultiSelect ? "primary" : "gray"}
              borderLight
            />
            <input
              hidden
              type="checkbox"
              ref={multiSelectRef}
              checked={isMultiSelect}
              onChange={(e) => setIsMultiSelect(e.target.checked)}
            />
          </>
        )}
      </AppBox>

      <AppBox flexDirection="column" style={{ height: "30rem", overflowY: "auto" }}>
        {searchedTeamPlayers.length > 0 ? (
          searchedTeamPlayers.map((tp) => renderPlayerRow(tp, selectedTeamPlayers.some((p) => p.id === tp.id)))
        ) : (
          <AppText>There are not any players</AppText>
        )}
      </AppBox>

      {isMultiSelect && (
        <AppBox justifyContent="end" gap="xs">
          <AppButton
            variant="outline"
            borderLight
            label="Clear"
            color="gray"
            onClick={() => setSelectedTeamPlayers([])}
          />
          <AppButton label="Add Players" onClick={() => onSelectedTeamPlayer?.(selectedTeamPlayers)} />
        </AppBox>
      )}
    </AppBox>
  );
}
